/**
 * @packageDocumentation
 * @module HackerAPI.User
 */
import { __extends } from "tslib";
import AddressList from "./address-list/address-list";
import EmergencyContact from "./emergency-contact/emergency-contact";
import Gender from "./gender/gender";
import Hacker from "./hacker/hacker";
import Mentor from "./mentor/mentor";
import RoleList from "./role-list/role-list";
import Sponsor from "./sponsor/sponsor";
import HackerAPIFetch from "../hackerapi-fetch/hackerapi-fetch";
import Base from "../base/base";
import Attendee from "../event/attendee/attendee";
import MailEvent from "./mail-event/mail-event";
import File from "../file/file";
import { parseDate, responseToDataUri } from "../utils/utils";
import Organization from "../organization/organization";
/**
 * Data container for user data with static methods for interfacing with hackerapi
 * @class
 * @extends { HackerAPI.Base }
 */
var User = /** @class */ (function (_super) {
    __extends(User, _super);
    /**
     * Creates a new User instance
     * @constructor
     * @param user
     */
    function User(_a) {
        var _b = _a === void 0 ? {} : _a, id = _b.id, name = _b.name, legal_name = _b.legal_name, legalName = _b.legalName, email = _b.email, bio = _b.bio, avatar = _b.avatar, phone_number = _b.phone_number, phoneNumber = _b.phoneNumber, food_restrictions = _b.food_restrictions, foodRestrictions = _b.foodRestrictions, allergies = _b.allergies, shirt_size = _b.shirt_size, shirtSize = _b.shirtSize, ethnicity = _b.ethnicity, native_language = _b.native_language, nativeLanguage = _b.nativeLanguage, date_of_birth = _b.date_of_birth, dateOfBirth = _b.dateOfBirth, age = _b.age, gender = _b.gender, organizations = _b.organizations, event_roles = _b.event_roles, eventRoles = _b.eventRoles, created_at = _b.created_at, createdAt = _b.createdAt, updated_at = _b.updated_at, updatedAt = _b.updatedAt, address = _b.address, emergency_contact = _b.emergency_contact, emergencyContact = _b.emergencyContact, roles = _b.roles;
        var _this = _super.call(this) || this;
        /**
         * The user's ID
         * @type { number }
         */
        _this.id = id;
        /**
         * The user's name
         * @type { string }
         */
        _this.name = name;
        /**
         * The user's legal name
         * @type { string }
         */
        _this.legalName = legal_name || legalName || null;
        /**
         * The user's email
         * @type { string }
         */
        _this.email = email;
        /**
         * The user's bio
         * @type { string }
         */
        _this.bio = bio || null;
        /**
         * The user's avatar
         * @type { string }
         */
        _this.avatar = avatar || null;
        /**
         * The user's phone number
         * @type { string }
         */
        _this.phoneNumber = phone_number || phoneNumber || null;
        /**
         * The user's food restrictions
         * @type { string[] }
         */
        _this.foodRestrictions = food_restrictions || foodRestrictions || [];
        /**
         * The user's allergies
         * @type { string[] }
         */
        _this.allergies = allergies || [];
        /**
         * The user's shirt size
         * @type { number }
         */
        _this.shirtSize = shirt_size || shirtSize || null;
        /**
         * The user's ethnicity
         * @type { string }
         */
        _this.ethnicity = ethnicity || null;
        /**
         * The user's native language
         * @type { string }
         */
        _this.nativeLanguage = native_language || nativeLanguage || null;
        /**
         * The user's date of birth. Kept as a string because dates are hard.
         * Let's assume the format will be YYYY-MM-DD.
         * @type { string }
         */
        _this.dateOfBirth = date_of_birth || dateOfBirth || null;
        /**
         * The user's age
         * @type { number }
         */
        _this.age = age || null;
        /**
         * The user's gender
         * @type { HackerAPI.User.Gender }
         */
        _this.gender = gender || null;
        /**
         * The list of organizations that the user belongs to
         * @todo Implement Organization
         * @type { HackerAPI.Organization[] }
         */
        _this.organizations = organizations
            ? organizations.map(function (org) { return new Organization(org); })
            : undefined;
        /**
         * The list of roles that the user has for various events
         * @type { HackerAPI.Event.Attendee[] }
         */
        _this.eventRoles =
            event_roles || eventRoles
                ? (event_roles || eventRoles).map(function (e) { return new Attendee(e); })
                : undefined;
        /**
         * When the user was created
         * @type { Date }
         */
        _this.createdAt = parseDate(created_at || createdAt);
        /**
         * When the user was last updated
         * @type { Date }
         */
        _this.updatedAt = parseDate(updated_at || updatedAt);
        /**
         * The addresses of the user
         * @type { HackerAPI.User.AddressList }
         */
        _this.address = address ? new AddressList(address) : undefined;
        /**
         * The emergency contact of the user
         * @type { HackerAPI.User.EmergencyContact }
         */
        _this.emergencyContact =
            emergency_contact || emergencyContact
                ? new EmergencyContact((emergency_contact || emergencyContact))
                : null;
        /**
         * The roles of the user
         * @type { HackerAPI.User.RoleList }
         */
        _this.roles = roles ? new RoleList(roles) : undefined;
        return _this;
    }
    Object.defineProperty(User, "AddressList", {
        /**
         * @returns
         */
        get: function () {
            return AddressList;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(User, "EmergencyContact", {
        /**
         * @returns
         */
        get: function () {
            return EmergencyContact;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(User, "Gender", {
        /**
         * @returns
         */
        get: function () {
            return Gender;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(User, "Hacker", {
        /**
         * @returns
         */
        get: function () {
            return Hacker;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(User, "Mentor", {
        /**
         * @returns
         */
        get: function () {
            return Mentor;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(User, "RoleList", {
        /**
         * @returns
         */
        get: function () {
            return RoleList;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(User, "Sponsor", {
        /**
         * @returns
         */
        get: function () {
            return Sponsor;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(User, "MailEvent", {
        /**
         * @returns
         */
        get: function () {
            return MailEvent;
        },
        enumerable: true,
        configurable: true
    });
    /**
     * Fetches a user from hackerapi
     * @param user
     * @param user.id
     * @param [token]
     * @returns
     */
    User.fetch = function (_a, token) {
        var id = (_a === void 0 ? { id: "me" } : _a).id;
        return HackerAPIFetch({
            path: "/v2/users/" + id,
            method: "GET",
            token: token
        }).then(function (data) { return new User(data); });
    };
    /**
     * Fetches the slack ID of a user
     * @param [user] - Gets the user by their token if undefined
     * @param user.id
     * @param [token]
     * @returns
     * @throws { HackerAPI.Error } - Throws an error if the user hasn't joined slack
     * @todo Better error handling for 404s
     */
    User.fetchSlackId = function (_a, token) {
        var id = (_a === void 0 ? { id: "me" } : _a).id;
        return HackerAPIFetch({
            path: "/v2/users/" + id + "/slack",
            method: "GET",
            token: token
        }).then(function (data) { return data.slack_id; });
    };
    /**
     * Fetches all the files belonging to a user, applying the appropriate filters
     * @see HackerAPI.File.fetchAllByUserId - This is just a wrapper function
     * @param user
     * @param user.id
     * @param [type]
     * @param [event]
     * @param event.slug
     * @param [token]
     * @returns
     */
    User.fetchFiles = function () {
        var args = [];
        for (var _i = 0; _i < arguments.length; _i++) {
            args[_i] = arguments[_i];
        }
        return File.fetchAllByUserId.apply(File, args);
    };
    /**
     * Fetches a user's badge for the event as a data uri
     * @param user
     * @param role - The user's role at the event
     * @param token
     * @returns
     */
    User.fetchBadge = function (_a, role, token) {
        var id = (_a === void 0 ? { id: "me" } : _a).id;
        if (role === void 0) { role = "hacker"; }
        return HackerAPIFetch({
            path: "/v2/users/" + id + "/badge",
            method: "GET",
            token: token,
            params: {
                role: role
            }
        }).then(responseToDataUri);
    };
    /**
     * Uploads an avatar for the user
     * @todo Test this bad boy
     * @param user
     * @param formData
     * @param formData.file - Only if formData is not already of type `FormData`
     * @param formData.type
     * @param token
     * @returns
     */
    User.uploadAvatar = function (_a, 
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    formData, token) {
        var id = (_a === void 0 ? { id: "me" } : _a).id;
        if (!(formData instanceof FormData)) {
            var type = formData.type, file = formData.file;
            formData = new FormData();
            formData.append("file", file);
            formData.append("type", type);
        }
        return HackerAPIFetch({
            path: "/v2/users/" + id + "/avatar",
            method: "POST",
            token: token,
            body: formData
        }).then(function (data) { return new File(data); });
    };
    /**
     * Creates a new user on hackerapi
     * @param user
     * @param [token]
     * @returns
     */
    User.create = function (user, _a) {
        var password = _a.password;
        user.password = password;
        return HackerAPIFetch({
            path: "/v2/users",
            method: "POST",
            body: user.toHackerAPIFormat()
        }).then(function (data) { return new User(data); });
    };
    /**
     * Updates a user on hackerapi
     * @param user
     * @param [auth]
     * @returns
     */
    User.update = function (user, token) {
        return HackerAPIFetch({
            path: "/v2/users/" + user.id,
            method: "PUT",
            body: user.toHackerAPIFormat(),
            token: token
        }).then(function (data) { return new User(data); });
    };
    /**
     * Updates a user on hackerapi based on the token they authenticate with
     * @param user
     * @param token
     * @returns
     */
    User.updateMe = function (user, token) {
        return HackerAPIFetch({
            path: "/v2/users/me",
            method: "PUT",
            body: user.toHackerAPIFormat(),
            token: token
        }).then(function (data) { return new User(data); });
    };
    /**
     * Deletes a user from hackerapi
     * @param user
     * @param user.id
     * @param [token]
     * @returns
     */
    User.delete = function (_a, token) {
        var id = _a.id;
        return HackerAPIFetch({
            path: "/v2/users/" + id,
            method: "DELETE",
            token: token
        });
    };
    return User;
}(Base));
export default User;
//# sourceMappingURL=user.js.map